import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageLoader, EmptyState } from "@/components/ui";
import { ApprovalCard, RaiseApprovalModal } from "@/components/approvals";
import type { Approval } from "@shared/types";
import type { ProjectBundle } from "../ProjectDetail";

export function ProjectApprovals({ bundle }: { bundle: ProjectBundle }) {
  const { can } = useAuth();
  const [status, setStatus] = useState("pending");
  const [raising, setRaising] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["approvals", "project", bundle.project.id, status],
    queryFn: () => api.get<{ approvals: Approval[] }>(`/approvals?scope=project&projectId=${bundle.project.id}&status=${status}`),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button key={s} onClick={() => setStatus(s)}
              className={`rounded-lg px-3 py-1.5 text-sm capitalize ${status === s ? "bg-brand-700 text-white" : "text-ink-soft hover:bg-surface-2"}`}>
              {s}
            </button>
          ))}
        </div>
        {can("approvals.request") && (
          <button className="btn-primary" onClick={() => setRaising(true)}><Plus size={15} /> Raise for approval</button>
        )}
      </div>

      {isLoading ? <PageLoader /> : !data?.approvals.length ? (
        <EmptyState title={`No ${status === "all" ? "" : status} approvals`}
          hint="Deliverable sign-offs and change requests for this project appear here." />
      ) : (
        <div className="space-y-3">
          {data.approvals.map((a) => (
            <ApprovalCard key={a.id} a={a} onChanged={refetch} canDecide={bundle.access.canDecideApprovals} />
          ))}
        </div>
      )}

      {raising && (
        <RaiseApprovalModal projectId={bundle.project.id} ownerName={bundle.project.ownerName} onClose={() => { setRaising(false); refetch(); }} />
      )}
    </div>
  );
}
